import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Calendar, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ThemeToggle } from '../components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginPage = () => {
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', confirmPassword: '' });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Veuillez saisir votre email');
      return;
    }
    setForgotLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success(r.data?.message || 'Email envoyé si le compte existe.');
      setForgotOpen(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Bienvenue !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.email || !registerData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (registerData.password.length < 6) {
      toast.error('Mot de passe trop court (min. 6 caractères)');
      return;
    }
    setLoading(true);
    try {
      await register(registerData.email, registerData.password);
      toast.success('Compte créé. En attente de validation par l\'administrateur.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex flex-col items-center justify-center p-6 gap-5">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <p
        className="font-outfit text-lg md:text-xl italic text-slate-700 dark:text-white tracking-wide animate-fade-in drop-shadow"
        data-testid="login-slogan"
      >
        Mon assistant de vie professionnel
      </p>
      <Card className="w-full max-w-md glass-card shadow-xl animate-fade-in" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Calendar className="w-8 h-8 text-[#065F46]" />
            <span className="font-outfit font-bold text-2xl tracking-tight text-[#065F46]">Ledgerly</span>
          </div>
          <CardTitle className="font-outfit text-xl text-slate-900">Bienvenue</CardTitle>
          <CardDescription className="text-slate-500">Gérez vos rendez-vous, devis et factures</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Inscription</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="owner@example.com"
                      className="pl-10 h-11 border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10 h-11 border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end -mt-2">
                  <button
                    type="button"
                    className="text-xs text-[#065F46] hover:text-[#047857] hover:underline font-medium"
                    onClick={() => setForgotOpen(true)}
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#065F46] hover:bg-[#047857] text-white font-medium btn-press"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Se connecter'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
                  ℹ️ Après inscription, votre compte devra être <b>validé par l'administrateur</b>. Vous recevrez un email dès l'activation.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="owner@example.com"
                      className="pl-10 h-11 border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-slate-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10 h-11 border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-confirm" className="text-slate-700">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="register-confirm"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10 h-11 border-slate-200 focus:border-[#065F46] focus:ring-[#065F46]"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      data-testid="register-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#065F46] hover:bg-[#047857] text-white font-medium btn-press"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer un compte'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>
              Saisissez l'email de votre compte. Vous recevrez un lien pour choisir un nouveau mot de passe (valide 1 heure).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fp-email" className="text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="fp-email"
                  type="email"
                  placeholder="vous@example.com"
                  className="pl-10 h-11"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Annuler</Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                className="bg-[#065F46] hover:bg-[#047857] text-white"
                data-testid="forgot-password-submit-btn"
              >
                {forgotLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Envoyer le lien
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
